import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import { getFilesListForBp } from 'services/fileService'
import { getObligationCandidateFiles, linkObligationEvidence } from 'services/ProgrammeService'

const TIER = {
  ready: { label: 'Ready', text: '#15803d', bg: '#dcfce7' },
  reclassify: { label: 'Will reclassify', text: '#d97706', bg: '#fef3c7' },
  needs_tags: { label: 'Needs tags', text: '#475569', bg: '#f1f5f9' },
  needs_date: { label: 'Needs a date', text: '#d97706', bg: '#fef3c7' },
}

const LinkEvidenceModal = ({ bpId, obligation, onClose, onLinked }) => {
  const [candidates, setCandidates] = useState([])
  const [loadingCandidates, setLoadingCandidates] = useState(true)
  const [browse, setBrowse] = useState(false)
  const [files, setFiles] = useState([])
  const [loadingFiles, setLoadingFiles] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let alive = true
    setLoadingCandidates(true)
    getObligationCandidateFiles(bpId, obligation.id)
      .then(res => {
        if (!alive) return
        const list = res.data?.data || []
        setCandidates(list)
        const best = list.find(c => !c.alreadyLinked)
        if (best) setSelectedId(best.fileId)
      })
      .catch(() => { if (alive) toast.error("Couldn't load suggested documents.") })
      .finally(() => { if (alive) setLoadingCandidates(false) })
    return () => { alive = false }
  }, [bpId, obligation.id])

  const loadAllFiles = () => {
    if (files.length || loadingFiles) { setBrowse(true); return }
    setBrowse(true)
    setLoadingFiles(true)
    getFilesListForBp({ id: bpId, query: { filter: { include_identity_documents: 1 } } })
      .then(res => setFiles(res.data?.data || res.data || []))
      .catch(() => toast.error("Couldn't load this building's documents."))
      .finally(() => setLoadingFiles(false))
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return files
    return files.filter(f => `${f.displayName || f.name || ''} ${f.fileType?.name || ''}`.toLowerCase().includes(q))
  }, [files, search])

  const selectedCandidate = candidates.find(c => c.fileId === selectedId)

  const submit = () => {
    if (!selectedId || saving) return
    setSaving(true)
    linkObligationEvidence(bpId, obligation.id, { fileId: selectedId })
      .then(res => {
        const satisfied = res?.data?.data?.satisfied
        if (satisfied) toast.success('Document linked. This obligation is now satisfied.')
        else toast.warning('Document linked, but it does not yet satisfy this obligation (it may be expired or still need tags).')
        onLinked?.()
      })
      .catch(e => toast.error(e?.response?.data?.message || 'Could not link that document.'))
      .finally(() => setSaving(false))
  }

  return createPortal(
    <Backdrop onClick={onClose}>
      <Panel onClick={e => e.stopPropagation()}>
        <Header>
          <Title>Link an existing document</Title>
          <Sub>
            Pick a document already on this building to satisfy <strong>{obligation.name}</strong>. We have
            ranked the best matches first.
          </Sub>
        </Header>

        {!browse && (
          <Body>
            <SectionLabel><AutoAwesomeRoundedIcon style={{ fontSize: 15, color: '#d97706' }} /> Suggested matches</SectionLabel>
            <List>
              {loadingCandidates ? (
                [0, 1, 2].map(i => <Skeleton key={i} />)
              ) : candidates.length === 0 ? (
                <Muted>No likely matches found. Browse all documents instead.</Muted>
              ) : candidates.map((c, i) => {
                const tier = TIER[c.tier] || TIER.reclassify
                return (
                  <Row key={c.fileId} $active={selectedId === c.fileId} onClick={() => setSelectedId(c.fileId)}>
                    <DescriptionRoundedIcon style={{ fontSize: 18, color: '#94a3b8' }} />
                    <RowText>
                      <RowName>
                        {c.displayName || 'Document'}
                        {i === 0 && !c.alreadyLinked && <BestTag>Best match</BestTag>}
                        {c.alreadyLinked && <BestTag $muted>Currently linked</BestTag>}
                      </RowName>
                      <RowMeta>
                        <Badge $text={tier.text} $bg={tier.bg}>{tier.label}</Badge>
                        {!c.inDate && <Badge $text='#b00020' $bg='#fdecea'>Expired</Badge>}
                        <ReasonText>{c.reason}</ReasonText>
                      </RowMeta>
                    </RowText>
                    <Radio $active={selectedId === c.fileId} />
                  </Row>
                )
              })}
            </List>
            <BrowseLink onClick={loadAllFiles}>Browse all documents instead →</BrowseLink>
          </Body>
        )}

        {browse && (
          <Body>
            <SearchWrap>
              <SearchRoundedIcon style={{ fontSize: 18, color: '#94a3b8' }} />
              <SearchInput placeholder='Search documents…' value={search} onChange={e => setSearch(e.target.value)} />
            </SearchWrap>
            <List>
              {loadingFiles ? (
                [0, 1, 2, 3].map(i => <Skeleton key={i} />)
              ) : filtered.length === 0 ? (
                <Muted>No documents found on this building.</Muted>
              ) : filtered.map(f => (
                <Row key={f.id} $active={selectedId === f.id} onClick={() => setSelectedId(f.id)}>
                  <DescriptionRoundedIcon style={{ fontSize: 18, color: '#94a3b8' }} />
                  <RowText>
                    <RowName>{f.displayName || f.name || 'Document'}</RowName>
                    {f.fileType?.name && <RowType>{f.fileType.name}</RowType>}
                  </RowText>
                  <Radio $active={selectedId === f.id} />
                </Row>
              ))}
            </List>
            <BrowseLink onClick={() => setBrowse(false)}>← Back to suggested matches</BrowseLink>
          </Body>
        )}

        {browse && selectedId && !selectedCandidate && (
          <Warn>This will reclassify the selected document as the type this obligation expects. If it is being used as evidence elsewhere, that will be re-checked.</Warn>
        )}
        {selectedCandidate && selectedCandidate.tier !== 'ready' && (
          <Warn>{selectedCandidate.reason}</Warn>
        )}

        <Footer>
          <Cancel onClick={onClose}>Cancel</Cancel>
          <Save disabled={!selectedId || saving} onClick={submit}>{saving ? 'Linking…' : 'Link as evidence'}</Save>
        </Footer>
      </Panel>
    </Backdrop>,
    document.body
  )
}

export default LinkEvidenceModal

const pulse = keyframes`0%{opacity:.55}50%{opacity:1}100%{opacity:.55}`

const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(12, 18, 41, 0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 100001; padding: 20px;
`

const Panel = styled.div`
  background: #fff; border-radius: 14px;
  width: 100%; max-width: 540px; max-height: calc(100vh - 48px);
  display: flex; flex-direction: column;
  box-shadow: 0 20px 48px rgba(0,0,0,0.2); overflow: hidden;
`

const Header = styled.div` padding: 20px 22px 12px; display: flex; flex-direction: column; gap: 6px; `

const Title = styled.h3`
  margin: 0; font-size: 16px; font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const Sub = styled.p`
  margin: 0; font-size: 12.5px; line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[700] || '#475569'};
`

const Body = styled.div` display: flex; flex-direction: column; flex: 1; min-height: 0; padding: 0 22px; `

const SectionLabel = styled.div`
  display: flex; align-items: center; gap: 6px;
  font-size: 11px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800] || '#6e6e78'};
  margin: 4px 0 8px;
`

const SearchWrap = styled.div`
  display: flex; align-items: center; gap: 8px; margin: 4px 0 0;
  padding: 8px 12px; border: 1px solid #ececec; border-radius: 10px;
`

const SearchInput = styled.input`
  font: inherit; flex: 1; min-width: 0; border: none; outline: none; background: transparent;
  font-size: 13.5px; color: ${({ theme }) => theme.colors.haiti};
`

const List = styled.div`
  margin: 12px 0; overflow-y: auto; flex: 1; min-height: 120px; max-height: 340px;
  display: flex; flex-direction: column; gap: 6px;
`

const Muted = styled.div` padding: 24px; text-align: center; font-size: 13px; color: #94a3b8; `

const Skeleton = styled.div`
  height: 54px; border-radius: 10px; background: #f1f5f9; animation: ${pulse} 1.4s ease-in-out infinite;
`

const Row = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 12px; border-radius: 10px; cursor: pointer;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.secondary : '#ececec')};
  background: ${({ $active, theme }) => ($active ? `${theme.colors.secondary}0F` : '#fff')};
  &:hover { border-color: #d8d8dc; }
`

const RowText = styled.div` flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; `

const RowName = styled.div`
  font-size: 13px; font-weight: 600; display: flex; align-items: center; gap: 6px;
  color: ${({ theme }) => theme.colors.haiti};
  overflow: hidden; text-overflow: ellipsis;
`

const RowType = styled.div` font-size: 11px; color: #94a3b8; `

const RowMeta = styled.div` display: flex; align-items: center; gap: 6px; flex-wrap: wrap; `

const Badge = styled.span`
  display: inline-block; padding: 1px 7px; border-radius: 9px; font-size: 10px; font-weight: 700;
  color: ${({ $text }) => $text}; background: ${({ $bg }) => $bg};
`

const BestTag = styled.span`
  display: inline-block; padding: 1px 7px; border-radius: 9px; font-size: 10px; font-weight: 700;
  color: ${({ $muted }) => ($muted ? '#64748b' : '#0369a1')};
  background: ${({ $muted }) => ($muted ? '#f1f5f9' : '#e0f2fe')};
`

const ReasonText = styled.span` font-size: 11px; color: #64748b; line-height: 1.35; `

const Radio = styled.span`
  width: 16px; height: 16px; border-radius: 50%; flex-shrink: 0; margin-top: 2px;
  border: 2px solid ${({ $active, theme }) => ($active ? theme.colors.secondary : '#cbd5e1')};
  background: ${({ $active, theme }) => ($active ? theme.colors.secondary : '#fff')};
  box-shadow: ${({ $active }) => ($active ? 'inset 0 0 0 2px #fff' : 'none')};
`

const BrowseLink = styled.button`
  font: inherit; background: none; border: none; cursor: pointer; align-self: flex-start;
  padding: 0 0 8px; font-size: 12px; font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
  &:hover { text-decoration: underline; }
`

const Warn = styled.div`
  margin: 0 22px; padding: 9px 12px; border-radius: 9px;
  font-size: 12px; line-height: 1.45;
  background: #fef3c7; border: 1px solid #fde68a; color: #92600a;
`

const Footer = styled.div`
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 16px 22px; border-top: 1px solid #f4f4f6; margin-top: 12px;
`

const Cancel = styled.button`
  font: inherit; background: #fff; border: 1px solid #ececec; border-radius: 8px;
  padding: 8px 16px; font-size: 13px; font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti}; cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const Save = styled.button`
  font: inherit; background: ${({ theme }) => theme.colors.secondary};
  color: #fff; border: none; border-radius: 8px;
  padding: 8px 18px; font-size: 13px; font-weight: 700; cursor: pointer;
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(1.06); }
`
